import React, { useRef, useEffect } from 'react';
import type { Mode2Result } from '../hooks/useMode2Generate';
import { getProxyUrl } from '../utils/proxyUrl';

interface ImageHistoryStripProps {
  history: Mode2Result[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

const ImageHistoryStrip: React.FC<ImageHistoryStripProps> = ({ history, selectedIndex, onSelect }) => {
  const stripRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the latest (rightmost) item when history grows
  useEffect(() => {
    if (stripRef.current) {
      stripRef.current.scrollLeft = stripRef.current.scrollWidth;
    }
  }, [history.length]);

  if (history.length < 2) return null;

  return (
    <div className="shrink-0">
      <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1.5 px-0.5">生成歷史紀錄</p>
      <div
        ref={stripRef}
        className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700"
      >
        {history.map((item, index) => {
          const isSelected = index === selectedIndex;
          const isLatest = index === history.length - 1;

          // 已刪除的圖片保留位置（歷史圖片切換以索引定位，抽掉會讓索引位移），
          // 但不可選取、也不可下載
          if (item.deleted) {
            return (
              <div
                key={index}
                className="relative shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center cursor-not-allowed"
                title="這張圖片已刪除"
              >
                <span className="text-[9px] text-slate-400 dark:text-slate-500">已刪除</span>
                <span className="absolute top-0.5 left-0.5 text-[9px] font-bold bg-black/30 text-white rounded px-1 leading-4">
                  #{index + 1}
                </span>
              </div>
            );
          }

          return (
            <button
              key={index}
              onClick={() => onSelect(index)}
              className={`relative shrink-0 w-16 h-16 rounded-xl overflow-hidden transition-all duration-150 ${
                isSelected
                  ? 'ring-2 ring-blue-500 ring-offset-1 shadow-md'
                  : 'opacity-60 hover:opacity-90 hover:ring-1 hover:ring-slate-300 dark:hover:ring-slate-600'
              }`}
            >
              <img
                src={getProxyUrl(item.imageUrl)}
                alt={`生成 #${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Sequence badge */}
              <span className="absolute top-0.5 left-0.5 text-[9px] font-bold bg-black/50 text-white rounded px-1 leading-4">
                #{index + 1}
              </span>
              {/* Latest badge */}
              {isLatest && (
                <span className="absolute bottom-0.5 right-0.5 text-[8px] font-bold bg-blue-500 text-white rounded px-1 leading-4">
                  最新
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ImageHistoryStrip;
